import { ArrowLeft, Cookie } from 'lucide-react';
import { useEffect } from 'react';

import { legalPaths } from '@/app/paths';
import { BRAND, BrandMark, pageTitle } from '@/components/Brand';
import { Card } from '@/design-system/patterns/Card';
import { Button } from '@/design-system/ui/Button';
import { LEGAL_DOC_KEYS, LEGAL_DOC_LABEL, type LegalDocKey, legalDocument } from '@escala/domain/legal';

import { useLegal } from './context';
import { LegalContent, versionLine } from './LegalContent';

const base = (import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '');

/**
 * A página pública de cada documento (`/privacidade`, `/termos`, `/cookies`), fora da
 * sessão: o endereço que vai nos e-mails e que qualquer pessoa abre antes de entrar.
 */
export default function LegalPage({ doc }: { doc: LegalDocKey }) {
  const { openCookies } = useLegal();
  const current = legalDocument(doc);

  useEffect(() => {
    document.title = pageTitle(current.title);
  }, [current.title]);

  return (
    <main className="flex min-h-dvh flex-col items-center gap-5.5 bg-page px-4 py-10 text-ink">
      <BrandMark size="sm" />
      <Card className="flex w-full max-w-180 flex-col gap-5 rounded-dialog p-6 shadow-dialog sm:p-8">
        <nav aria-label="Documentos" className="flex flex-wrap gap-1.5">
          {LEGAL_DOC_KEYS.map((key) => (
            <a
              key={key}
              href={`${base}${legalPaths[key]}`}
              aria-current={key === doc ? 'page' : undefined}
              className="rounded-control-sm px-3 py-1.5 text-small font-semibold text-ink-subtle hover:text-ink aria-[current=page]:bg-primary-soft aria-[current=page]:text-primary-hover"
            >
              {LEGAL_DOC_LABEL[key].short}
            </a>
          ))}
        </nav>
        <header className="flex flex-col gap-1">
          <h1 className="m-0 text-title-sm font-extrabold tracking-[-0.03em]">{current.title}</h1>
          <p className="m-0 text-meta text-ink-subtle">
            {BRAND.full} · {versionLine(current)}
          </p>
        </header>
        <LegalContent doc={current} />
        <div className="flex flex-wrap gap-2 border-t border-line-divider pt-4">
          <Button asChild variant="secondary">
            <a href={`${base}/`}>
              <ArrowLeft aria-hidden />
              Ir para o {BRAND.full}
            </a>
          </Button>
          <Button variant="ghost" onClick={openCookies}>
            <Cookie aria-hidden />
            Gerenciar cookies
          </Button>
        </div>
      </Card>
      <p className="m-0 text-caption text-ink-placeholder">
        {BRAND.full} · {BRAND.tagline}
      </p>
    </main>
  );
}
